import { Download, Check, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { format } from "date-fns"

type PaymentSuccessProps = {
	transactionId: string
	amount: string
	currency: string
	paymentPreference: string
	onDownloadReceipt: () => void
	isLoading: boolean
	hideDownloadReceipt: boolean
	paidAt: string
}

const SidePerforatedSeparator = () => (
	<div className="relative my-6 flex items-center justify-between">
		{/* Left Circle */}
		<div className="absolute left-0 flex h-10 w-5 items-center justify-center">
			<div className="bg-card h-full w-full rounded-r-full"></div>
		</div>

		{/* Dotted Line */}
		<div
			className="mx-6 h-[1px] w-[calc(100%-4rem)] sm:mx-8"
			style={{
				backgroundImage:
					"repeating-linear-gradient(to right, #d1d5db 0, #d1d5db 8px, transparent 8px, transparent 16px)",
				backgroundSize: "100% 1px",
				backgroundRepeat: "no-repeat",
				backgroundPosition: "center",
			}}
		></div>

		{/* Right Circle */}
		<div className="absolute right-0 flex h-10 w-5 items-center justify-center">
			<div className="bg-card h-full w-full rounded-l-full"></div>
		</div>
	</div>
)

const BottomPerforations = () => {
	const PERFORATION_COUNT = 15

	return (
		<div
			className="absolute bottom-0 left-0 right-0 h-8"
			style={{ transform: "translateY(50%)" }}
		>
			<div className="flex h-full w-full justify-between px-1">
				{Array.from({ length: PERFORATION_COUNT }).map((_, index) => (
					<div
						key={index}
						className="bg-card h-8 w-8 flex-shrink-0 rounded-full"
					/>
				))}
			</div>
		</div>
	)
}

export const PaymentSuccess = ({
	transactionId,
	amount,
	currency,
	paymentPreference = "SEMESTER WISE",
	onDownloadReceipt,
	isLoading,
	hideDownloadReceipt,
	paidAt,
}: PaymentSuccessProps) => {
	const formattedDate = format(
		new Date(paidAt ? paidAt : new Date()),
		"dd MMM yyyy hh:mm a",
	).toUpperCase()

	return (
		<div className="relative w-full overflow-hidden rounded-tl-xl rounded-tr-xl bg-white text-center">
			{/* Header Content */}
			<div className="p-8 pb-0 sm:p-10">
				<div className="mb-6">
					<div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
						<div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-500 shadow-lg">
							<Check className="h-7 w-7 stroke-2 text-white" />
						</div>
					</div>
					<h1 className="mb-2 text-3xl font-extrabold text-gray-800">
						Thank You
					</h1>
					<p className="text-base text-gray-600">
						Your payment has been processed successfully
					</p>
				</div>
			</div>

			<SidePerforatedSeparator />

			{/* Details Content */}
			<div className="p-8 pb-12 pt-0 sm:p-10">
				{/* Primary Details */}
				<div className="mb-6 text-left">
					<div className="grid grid-cols-2 gap-4">
						<div className="flex flex-col space-y-1">
							<div className="text-xs uppercase tracking-wider text-gray-400">
								TRANSACTION ID
							</div>
							<div className="text-sm font-medium text-gray-700">
								{transactionId}
							</div>
						</div>

						<div className="flex flex-col items-end space-y-1">
							<div className="text-xs uppercase tracking-wider text-gray-400">
								AMOUNT
							</div>
							<div className="text-lg font-bold text-gray-800">
								{currency} {amount}
							</div>
						</div>
					</div>
				</div>

				{/* Fee Payment Preference */}
				<div className="mt-4 space-y-4 border-t border-gray-100 pt-4 text-left">
					<div className="flex flex-col space-y-1">
						<div className="text-xs uppercase tracking-wider text-gray-400">
							FEE PAYMENT PREFERENCE
						</div>
						<div className="text-sm font-medium text-gray-700">
							{paymentPreference}
						</div>
					</div>
				</div>

				{/* Time & Date */}
				<div className="mt-4 space-y-4 border-t border-gray-100 pt-4 text-left">
					<div className="flex flex-col space-y-1">
						<div className="text-xs uppercase tracking-wider text-gray-400">
							TIME & DATE
						</div>
						<div className="text-sm font-medium text-gray-700">
							{formattedDate}
						</div>
					</div>
				</div>

				{/* Download Button */}
				<div
					className="relative mx-[-2rem] mt-8 px-8 pt-6 sm:mx-[-2.5rem] sm:px-10"
					style={{
						backgroundImage:
							"repeating-linear-gradient(to right, #d1d5db 0, #d1d5db 8px, transparent 8px, transparent 16px)",
						backgroundSize: "100% 1px",
						backgroundRepeat: "no-repeat",
						backgroundPosition: "left top",
					}}
				>
					<Button
						variant="customDefault"
						type="button"
						className="flex w-full items-center justify-center space-x-2 py-3 text-lg font-semibold text-green-600"
						onClick={onDownloadReceipt}
						disabled={hideDownloadReceipt}
					>
						{isLoading ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								<span>Downloading Receipt...</span>
							</>
						) : (
							<>
								<Download className="h-5 w-5" />
								<span>Get PDF Receipt</span>
							</>
						)}
					</Button>
				</div>
			</div>

			<BottomPerforations />
		</div>
	)
}

export default PaymentSuccess
